/**
 * Client-side slicing for the admin search-usage dashboard.
 *
 * The dashboard fetches ONE thing — ``GET /api/analytics/search/dataset``,
 * every ``SEARCH_QUERY_LOG`` row in the window — and computes every view from
 * it in memory. Slicer changes (user, surface, provider, zero-result, flagged,
 * group-by, granularity) therefore never re-hit Snowflake; only a date-range
 * change refetches. The dataset is small by construction (an internal tool's
 * search log, capped server-side with an explicit ``truncated`` flag).
 *
 * Everything here is a pure function of the rows so the panel can memoize
 * each view with ``useMemo``.
 */

import type {
  GradeVolume,
  Granularity,
  RequestedTagDimension,
  SearchGroupBy,
  SearchProviderOption,
  SearchQueryRow,
  SearchSummary,
  SearchSurface,
  SearchUser,
  TagVolume,
  TimeBucket,
} from "./types";

// Analytics reads are aggregates over slow-moving data — cache them well past
// the 30s app default so the startup prefetch (Layout) still counts as fresh
// when the tab is opened, and toggling slicers/routes never refetches.
export const ANALYTICS_STALE_MS = 5 * 60 * 1000;

export const SEARCH_DATASET_PATH = "/api/analytics/search/dataset";

// Shared between the panel's useQuery and Layout's startup prefetch — the keys
// must match exactly or the prefetch warms a cache entry nobody reads.
export function searchDatasetKey(start: string, end: string) {
  return ["search_usage", "dataset", { start, end }] as const;
}

// Default window for the Analytics tab, shared by the page's date inputs and
// the startup prefetch so their query keys line up: last 180 days ending
// today, formatted as YYYY-MM-DD for <input type="date">.
export function analyticsDefaultStart(): string {
  const d = new Date();
  d.setDate(d.getDate() - 180);
  return d.toISOString().slice(0, 10);
}

export function analyticsTodayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

/**
 * Everything the Analytics tab renders on first paint, as (queryKey, request)
 * specs. Layout prefetches these at app startup (admins only) so the tab —
 * and every in-memory search-usage slicer — is ready before it's ever opened;
 * the page's own useQuery calls then read the warmed cache instead of hitting
 * Snowflake. Each spec's queryKey must stay byte-identical to the matching
 * useQuery key in AnalyticsPage.tsx (same default window/granularity/dimension
 * — that's why the defaults live in this module), or the prefetch warms an
 * entry nobody reads. TanStack's key hash drops undefined members, so the
 * page's ``statusesParam: undefined`` (qual-intro default) hashes equal to the
 * keys below.
 */
export interface AnalyticsPrefetchSpec {
  queryKey: readonly unknown[];
  path: string;
  query: Record<string, string | number>;
}

export function analyticsPrefetchSpecs(): AnalyticsPrefetchSpec[] {
  const start = analyticsDefaultStart();
  const end = analyticsTodayIso();
  const granularity = "week"; // the page's defaults
  const dimension = "service";
  return [
    // The search-usage dataset — the big win: every slicer on the panel works
    // off this one response.
    { queryKey: searchDatasetKey(start, end), path: SEARCH_DATASET_PATH, query: { start, end } },
    // Corpus panels' default views (v3 / v5 / qual-intro).
    {
      queryKey: ["v3", "over-time", { start, end, granularity }],
      path: "/api/analytics/v3/quals-over-time",
      query: { start, end, granularity },
    },
    {
      queryKey: ["v3", "grade", { start, end }],
      path: "/api/analytics/v3/grade-volumes",
      query: { start, end },
    },
    {
      queryKey: ["v3", "source", { start, end }],
      path: "/api/analytics/v3/source-volumes",
      query: { start, end },
    },
    {
      queryKey: ["v3", "tags", { start, end, dimension }],
      path: "/api/analytics/v3/tag-volumes",
      query: { start, end, dimension, limit: 15 },
    },
    {
      queryKey: ["v5_ee", "over-time", { start, end, granularity }],
      path: "/api/analytics/v5/ee-over-time",
      query: { start, end, granularity },
    },
    {
      queryKey: ["v5_ee", "grade", { start, end }],
      path: "/api/analytics/v5/grade-volumes",
      query: { start, end },
    },
    {
      queryKey: ["v5_ee", "tags", { start, end, dimension }],
      path: "/api/analytics/v5/tag-volumes",
      query: { start, end, dimension, limit: 15 },
    },
    {
      queryKey: ["qual_intro", "over-time", { start, end, granularity }],
      path: "/api/analytics/qual-intro/intros-over-time",
      query: { start, end, granularity },
    },
    {
      queryKey: ["qual_intro", "status", { start, end }],
      path: "/api/analytics/qual-intro/status-volumes",
      query: { start, end },
    },
  ];
}

export const SURFACE_LABELS: Record<SearchSurface, string> = {
  web: "Web tab",
  mcp: "MCP widget",
};

/** Which surface a row ran on. The web tab writes SOURCE='search_chat'; MCP
 * tools write an ``mcp_``-prefixed SOURCE. Anything else (future surfaces)
 * falls through as its raw source string so it stays visible. */
export function rowSurface(row: SearchQueryRow): SearchSurface | "other" {
  if (row.source === "search_chat") return "web";
  if (row.source?.startsWith("mcp_")) return "mcp";
  return "other";
}

function surfaceLabel(row: SearchQueryRow): string {
  const s = rowSurface(row);
  return s === "other" ? (row.source ?? "(unknown)") : SURFACE_LABELS[s];
}

function userLabel(row: SearchQueryRow): string {
  return row.user_name ?? row.user_upn ?? row.user_oid ?? "(unknown)";
}

/** The panel-wide scope: user and/or surface. "" means unscoped. */
export function scopeRows(
  rows: SearchQueryRow[],
  scope: { userOid?: string; surface?: "" | SearchSurface },
): SearchQueryRow[] {
  const { userOid, surface } = scope;
  if (!userOid && !surface) return rows;
  return rows.filter(
    (r) => (!userOid || r.user_oid === userOid) && (!surface || rowSurface(r) === surface),
  );
}

/**
 * Truncate an ISO timestamp to its day / ISO-week (Monday) / month bucket,
 * matching Snowflake's ``DATE_TRUNC`` semantics the server-side charts used.
 * Operates on the date part as UTC so the local timezone can't shift a row
 * across a bucket boundary.
 */
export function truncateBucket(searchedAt: string, granularity: Granularity): string {
  const day = searchedAt.slice(0, 10);
  if (granularity === "day") return day;
  if (granularity === "month") return `${day.slice(0, 7)}-01`;
  const dt = new Date(`${day}T00:00:00Z`);
  const sinceMonday = (dt.getUTCDay() + 6) % 7; // Sun=0 → Mon-based index
  dt.setUTCDate(dt.getUTCDate() - sinceMonday);
  return dt.toISOString().slice(0, 10);
}

/** Headline tile stats. Zero-result excludes flagged rows — a refused
 * (bulk-export/burst) query returning 0 is a different signal than an unmet
 * search. ``total_results`` sums RESULT_COUNT (items returned, not distinct). */
export function summarize(rows: SearchQueryRow[]): SearchSummary {
  const users = new Set<string>();
  const providers = new Set<string>();
  let totalResults = 0;
  let zero = 0;
  let flagged = 0;
  let web = 0;
  let mcp = 0;
  for (const r of rows) {
    if (r.user_oid) users.add(r.user_oid);
    for (const p of r.result_providers) providers.add(p.id);
    totalResults += r.result_count ?? 0;
    if (r.flagged) flagged += 1;
    else if (!r.result_count) zero += 1;
    const s = rowSurface(r);
    if (s === "web") web += 1;
    else if (s === "mcp") mcp += 1;
  }
  return {
    total_queries: rows.length,
    distinct_users: users.size,
    total_results: totalResults,
    zero_result_queries: zero,
    flagged_queries: flagged,
    web_queries: web,
    mcp_queries: mcp,
    distinct_providers: providers.size,
  };
}

function toTimeBuckets(counts: Map<string, Map<string, number>>): TimeBucket[] {
  const out: TimeBucket[] = [];
  for (const [bucket, groups] of counts) {
    for (const [group, count] of groups) {
      out.push({ bucket, group_key: group, count });
    }
  }
  // ISO dates sort lexicographically; stable group order keeps series colors
  // steady across slicer changes.
  return out.sort(
    (a, b) =>
      a.bucket.localeCompare(b.bucket) || (a.group_key ?? "").localeCompare(b.group_key ?? ""),
  );
}

function bumpBucket(counts: Map<string, Map<string, number>>, bucket: string, group: string, by = 1) {
  let groups = counts.get(bucket);
  if (!groups) counts.set(bucket, (groups = new Map()));
  groups.set(group, (groups.get(group) ?? 0) + by);
}

/** Query volume over time, one series per user or per surface. */
export function overTime(
  rows: SearchQueryRow[],
  granularity: Granularity,
  groupBy: SearchGroupBy,
): TimeBucket[] {
  const counts = new Map<string, Map<string, number>>();
  for (const r of rows) {
    const bucket = truncateBucket(r.searched_at, granularity);
    bumpBucket(counts, bucket, groupBy === "user" ? userLabel(r) : surfaceLabel(r));
  }
  return toTimeBuckets(counts);
}

function topCounts(counts: Map<string, number>, limit: number): [string, number][] {
  return [...counts.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0])).slice(0, limit);
}

/** Top searchers by query count. */
export function byUser(rows: SearchQueryRow[], limit: number): GradeVolume[] {
  const counts = new Map<string, number>();
  for (const r of rows) {
    const label = userLabel(r);
    counts.set(label, (counts.get(label) ?? 0) + 1);
  }
  return topCounts(counts, limit).map(([label, count]) => ({ label, count }));
}

/** What came back: quals returned per provider (summing per-query counts). */
export function providerPulls(rows: SearchQueryRow[], limit: number): TagVolume[] {
  const counts = new Map<string, number>();
  for (const r of rows) {
    for (const p of r.result_providers) {
      counts.set(p.name, (counts.get(p.name) ?? 0) + p.count);
    }
  }
  return topCounts(counts, limit).map(([tag, count]) => ({ tag, count }));
}

/** What users ASK for: top requested service / industry / provider values.
 * Rows without that scope field are skipped — the chart shows expressed
 * intent, not a null bar. */
export function requestedTags(
  rows: SearchQueryRow[],
  dimension: RequestedTagDimension,
  limit: number,
): TagVolume[] {
  const counts = new Map<string, number>();
  for (const r of rows) {
    const value =
      dimension === "service"
        ? r.requested_service
        : dimension === "industry"
          ? r.requested_industry
          : r.requested_provider;
    if (!value) continue;
    // Multi-value scope (the planner may hedge across several taxonomy values,
    // OR semantics) is logged " | "-joined — count each value separately.
    for (const part of value.split(" | ")) {
      const v = part.trim();
      if (v) counts.set(v, (counts.get(v) ?? 0) + 1);
    }
  }
  return topCounts(counts, limit).map(([tag, count]) => ({ tag, count }));
}

/** Distinct searchers in the rows — the user-slicer dropdown. */
export function userOptions(rows: SearchQueryRow[]): SearchUser[] {
  const byOid = new Map<string, string>();
  for (const r of rows) {
    if (r.user_oid && !byOid.has(r.user_oid)) byOid.set(r.user_oid, userLabel(r));
  }
  return [...byOid.entries()]
    .map(([oid, label]) => ({ oid, label }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

/** Distinct providers pulled in the rows — the deep-dive dropdown. */
export function providerOptions(rows: SearchQueryRow[]): SearchProviderOption[] {
  const byId = new Map<string, string>();
  for (const r of rows) {
    for (const p of r.result_providers) {
      if (!byId.has(p.id)) byId.set(p.id, p.name);
    }
  }
  return [...byId.entries()]
    .map(([id, label]) => ({ id, label }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

/** One provider's pull volume over time, one series per user — who is pulling
 * this provider's quals, and when. */
export function providerOverTime(
  rows: SearchQueryRow[],
  providerId: string,
  granularity: Granularity,
): TimeBucket[] {
  const counts = new Map<string, Map<string, number>>();
  for (const r of rows) {
    const pull = r.result_providers.find((p) => p.id === providerId);
    if (!pull) continue;
    bumpBucket(counts, truncateBucket(r.searched_at, granularity), userLabel(r), pull.count);
  }
  return toTimeBuckets(counts);
}

/** The queries table: optional provider / zero-result / flagged narrowing.
 * Rows are already newest-first from the server; keep that order. */
export function filterTable(
  rows: SearchQueryRow[],
  opts: { providerId?: string; zeroOnly?: boolean; flaggedOnly?: boolean },
): SearchQueryRow[] {
  const { providerId, zeroOnly, flaggedOnly } = opts;
  return rows.filter(
    (r) =>
      (!providerId || r.result_providers.some((p) => p.id === providerId)) &&
      (!zeroOnly || !r.result_count) &&
      (!flaggedOnly || r.flagged),
  );
}
